import { Injectable, inject } from '@angular/core';
import {TenantService} from './tenant.service';
import {from, Observable, forkJoin, EMPTY, of} from 'rxjs';
import {AssignmentSolution, AssignmentSolutionGrade} from '../models/assignment-solution.model';
import {first, map, catchError} from 'rxjs/operators';
import { createFirestoreId } from '../common/firestore-utils';
import { Firestore, QueryConstraint, collection, collectionData, doc, getDocsFromServer, getDoc, limit, orderBy, query, setDoc, startAfter, updateDoc, where, writeBatch, deleteDoc } from '@angular/fire/firestore';
import {FileUploadService} from './file-upload.service';


@Injectable({
  providedIn: 'root'
})
export class AssignmentSolutionsDbService {
  private tenant = inject(TenantService);
  private firestore = inject(Firestore);
  private fileUpload = inject(FileUploadService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {
  }


  assignmentSolutionsPath() {
    return `schools/${this.tenant.id}/assignmentSolutions`;
  }


  /**
   * How many submissions an assignment lesson has, and how many are still waiting on the creator.
   *
   * A one-off read rather than a live subscription: this feeds a summary in the lesson editor, where the
   * numbers only need to be right when the panel is opened, and a standing listener on every assignment
   * lesson would cost far more than it is worth.
   *
   * Counted client-side because the pinned Firestore SDK has no aggregation query, and because both
   * numbers come from the same single read anyway.
   */
  countSolutionsForLesson(lessonId: string): Observable<{ total: number; awaitingReview: number }> {

    const solutionsQuery = query(
      collection(this.firestore, this.assignmentSolutionsPath()),
      where('lessonId', '==', lessonId));

    return from(getDocsFromServer(solutionsQuery)).pipe(
      map(snapshot => {
        const statuses = snapshot.docs.map(d => d.data()?.['status']);

        return {
          total: statuses.length,
          awaitingReview: statuses.filter(s => s === 'Submitted').length,
        };
      }),
      catchError(() => of({ total: 0, awaitingReview: 0 })));
  }


  createNewAssigmentSolution(solution: Partial<AssignmentSolution>): Observable<AssignmentSolution> {

    const assigmentSolutionId = createFirestoreId(this.firestore)

    const assignmentSolutionPath = `${this.assignmentSolutionsPath()}/${assigmentSolutionId}`;

    const assignmentSolutionPathDocRef = doc(this.firestore, assignmentSolutionPath)

    const assignmentSolutionPathAsync = setDoc(assignmentSolutionPathDocRef, solution)

    return from(assignmentSolutionPathAsync)
      .pipe(
        map(() => {
          return <AssignmentSolution>{
            id: assigmentSolutionId,
            ...solution
          };
        })
      );
  }

  // Returns the latest solution for a user/lesson, or null if none exists

  loadLatestAssignmentSolutionForLesson(lessonId: string, userId: string): Observable<AssignmentSolution> {

    const constraints = [
      where('lessonId', '==', lessonId),
      where('userId', '==', userId),
      orderBy('submittedAt', 'desc'),
      limit(1)
    ]

    const query$ = collectionData(query(collection(this.firestore, `${this.assignmentSolutionsPath()}`), ...constraints), {idField: 'id'}).pipe(first());

    return query$
      .pipe(
        map(results => {

          if(results.length === 0) {
            return null
          }

          return <AssignmentSolution>{
            id: results[0].id,
            ...results[0]
          };

        })
      );
  }


  // loadAllAssignmentsSolution(pageSize: number, startAfterQuery?) {

  //   const constraints: QueryConstraint[] = [
  //     orderBy("submittedAt", "desc"),
  //     send-limit-email(pageSize)
  //   ]

  //   if(startAfterQuery) {
  //     constraints.push(startAfter(startAfterQuery))
  //   }

  //   const query$ = collectionData(query(collection(this.firestore, `${this.assignmentSolutionsPath()}`), ...constraints), {idField: 'id'})

  loadAssignmentsSolutionPage(pageSize: number, startAfterQuery?, status?:string) {
    const path = this.assignmentSolutionsPath();

    const constraints: QueryConstraint[] = [
      orderBy("submittedAt", "desc"),
      limit(pageSize)
    ]
    if(startAfterQuery) {
      constraints.push(startAfter(startAfterQuery))
    }
    if (status != 'All') {
      constraints.push(where('status', '==', status));
    }

    const q = query(collection(this.firestore, path), ...constraints);
    return from(getDocsFromServer(q)).pipe(
      map(snapshot =>
        snapshot.docs.map(docSnap => {
          const data = docSnap.data() as Record<string, unknown>;
          return { id: docSnap.id, ...data } as AssignmentSolution;
        })
      )
    );
  }

  updateAssignmentSolutionStatus(solution: AssignmentSolution, status: string) {

    const {userId, courseId, lessonId} = solution;

    const batch = writeBatch(this.firestore);

    const assignmentSolutionRef = doc(this.firestore, `${this.assignmentSolutionsPath()}/${solution.id}`);

    batch.update(assignmentSolutionRef, {status});

    if (status == "Approved") {

      const lessonStatusRef = doc(this.firestore, `schools/${this.tenant.id}/users/${userId}/lessonsWatchedPerCourse/${courseId}/lessonsWatched/${lessonId}`)

      batch.set(lessonStatusRef, {
        watched:true
      },
        {
          merge:true
        });

    }

    return batch.commit();
  }

  /**
   * Save a reviewer's score against a submission.
   *
   * Separate from `updateAssignmentSolutionStatus` on purpose: approving and scoring are two different
   * decisions, and a reviewer can do either without the other. Approving still completes the lesson
   * exactly as it always has, and grading never touches the status, so nothing about the existing
   * Approve/Reject flow changes.
   *
   * `null` rather than omission for every field, so "clear this grade" actually clears it — Firestore
   * rejects `undefined`, and an omitted key would leave the old score in place.
   */
  updateAssignmentSolutionGrade(solution: AssignmentSolution, grade: AssignmentSolutionGrade) {

    const assignmentSolutionRef = doc(this.firestore, `${this.assignmentSolutionsPath()}/${solution.id}`);

    return updateDoc(assignmentSolutionRef, {
      score: grade.score ?? null,
      maxScore: grade.maxScore ?? null,
      rubricScores: grade.rubricScores ?? null,
      gradedAt: grade.gradedAt ?? null
    });
  }

  getAssignmentSolutionById(id: string): Observable<AssignmentSolution | null> {
    const assignmentSolutionRef = doc(this.firestore, `${this.assignmentSolutionsPath()}/${id}`);
    return from(getDoc(assignmentSolutionRef)).pipe(
      map(docSnap => {
        if (!docSnap.exists()) {
          return null;
        }
        const data = docSnap.data() as Record<string, unknown>;
        return { id: docSnap.id, ...data } as AssignmentSolution;
      })
    );
  }

  // Extracts storage path from Firebase Storage download URL
  private extractStoragePathFromUrl(downloadUrl: string): string | null {
    try {
      const url = new URL(downloadUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+?)(?:\?|$)/);
      if (pathMatch && pathMatch[1]) {
        const decodedPath = decodeURIComponent(pathMatch[1]);
        console.log('Extracted storage path:', decodedPath);
        return decodedPath;
      }
      console.warn('Could not extract storage path from URL:', downloadUrl);
      return null;
    } catch (error) {
      console.error('Error extracting storage path from URL:', error, downloadUrl);
      return null;
    }
  }

  deleteAssignmentSolution(solution: AssignmentSolution) {
    const assignmentSolutionRef = doc(this.firestore, `${this.assignmentSolutionsPath()}/${solution.id}`);
    return deleteDoc(assignmentSolutionRef);
  }

  // Deletes both Firestore document and Storage file
  deleteAssignmentSolutionWithFile(solution: AssignmentSolution): Observable<void> {
    const storagePath = this.extractStoragePathFromUrl(solution.solutionFileUrl);
    
    console.log('Deleting assignment solution:', {
      id: solution.id,
      solutionFileUrl: solution.solutionFileUrl,
      extractedStoragePath: storagePath
    });
    
    const deleteFile$ = storagePath 
      ? this.fileUpload.deleteFile(storagePath).pipe(
          catchError(err => {
            console.error('Failed to delete assignment file from storage:', {
              storagePath,
              error: err
            });
            throw err;
          })
        )
      : of(undefined);

    const deleteDoc$ = from(this.deleteAssignmentSolution(solution)).pipe(
      catchError(err => {
        console.error('Failed to delete assignment solution document:', err);
        throw err;
      })
    );

    return forkJoin([deleteFile$, deleteDoc$]).pipe(
      map(() => {
        console.log('Assignment solution deleted successfully:', {
          id: solution.id,
          fileDeleted: !!storagePath
        });
        return undefined;
      })
    );
  }

  createNewAssignmentMessage(id: string, message: {}) {

    const assigmentMessageId = createFirestoreId(this.firestore);

    const newAssignmentMessageDocRef = doc(this.firestore, `${this.assignmentSolutionsPath()}/${id}/assignmentMessages/${assigmentMessageId}`)
    const newAssignmentMessageAsync= setDoc(newAssignmentMessageDocRef, {message});

    return newAssignmentMessageAsync

  }

}
